/**
 * Teams list page (route /teams). Operator picks a venue, then sees the
 * canonical (persistent) team identities for that venue with rollup stats,
 * sorted by visits. Rows link into the team detail page. A banner surfaces
 * the count of still-unassociated per-event rows and links to the associate
 * view, where weekly name-changers get grouped into one team.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Callout,
  Dialog,
  Flex,
  Heading,
  Table,
  Text,
  TextField,
} from '@radix-ui/themes';
import VenuePicker from './VenuePicker';
import {
  createTeam,
  fetchTeams,
  fetchVenueOptions,
  fmtDate,
  fmtNum,
  type TeamSummary,
  type VenueOption,
} from './types';
import './Teams.css';

const TeamsList: React.FC = () => {
  const navigate = useNavigate();
  const [venues, setVenues] = useState<VenueOption[]>([]);
  const [venueId, setVenueId] = useState<number | null>(null);
  const [teams, setTeams] = useState<TeamSummary[]>([]);
  const [unassociatedCount, setUnassociatedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  // Load venues once; default to the first venue.
  useEffect(() => {
    let active = true;
    fetchVenueOptions()
      .then((vs) => {
        if (!active) return;
        setVenues(vs);
        if (vs[0]) setVenueId(vs[0].id);
        else setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setError('Failed to load venues.');
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const load = useCallback(async (vid: number) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchTeams(vid);
      setTeams(data.teams ?? []);
      setUnassociatedCount(data.unassociatedCount ?? 0);
    } catch {
      setError('Failed to load teams for this venue.');
      setTeams([]);
      setUnassociatedCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (venueId !== null) load(venueId);
  }, [venueId, load]);

  const sorted = useMemo(
    () => [...teams].sort((a, b) => b.visits - a.visits),
    [teams],
  );

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name || venueId === null) return;
    setSaving(true);
    try {
      await createTeam(name, venueId);
      setNewName('');
      setNewOpen(false);
      await load(venueId);
    } catch {
      setError('Failed to create team.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="teams-page">
      <div className="teams-header">
        <div>
          <Heading size="6">Teams</Heading>
          <Text size="2" color="gray">
            Persistent team identities and their attendance &amp; performance.
          </Text>
        </div>
        <Flex gap="2" align="center">
          <Button
            variant="soft"
            onClick={() => venueId !== null && navigate(`/teams/associate?venueId=${venueId}`)}
            disabled={venueId === null}
          >
            Associate rows
          </Button>
          <Button onClick={() => setNewOpen(true)} disabled={venueId === null}>
            + New team
          </Button>
        </Flex>
      </div>

      <div className="teams-toolbar">
        <Text size="2" color="gray">
          Venue:
        </Text>
        <VenuePicker venues={venues} value={venueId} onChange={setVenueId} />
      </div>

      {error && <div className="teams-error">{error}</div>}

      {unassociatedCount > 0 && venueId !== null && (
        <Callout.Root mb="3" color="amber" role="status">
          <Callout.Text>
            {unassociatedCount} per-event team{unassociatedCount === 1 ? '' : 's'} not yet
            associated with a persistent identity.{' '}
            <Text
              as="span"
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => navigate(`/teams/associate?venueId=${venueId}`)}
            >
              Associate them
            </Text>
            .
          </Callout.Text>
        </Callout.Root>
      )}

      {loading ? (
        <div className="teams-empty">Loading teams…</div>
      ) : sorted.length === 0 ? (
        <div className="teams-empty">
          <Text as="p" size="3" weight="medium">
            No teams yet for this venue.
          </Text>
          <Text as="p" size="2" color="gray">
            Create a team, or use “Associate rows” to group per-event teams into one identity.
          </Text>
        </div>
      ) : (
        <Table.Root variant="surface">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Team</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell align="center">Visits</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell align="center">Name variants</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell align="center">Avg players</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell align="center">Avg place</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell align="center">Wins</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell align="right">Last seen</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {sorted.map((t) => (
              <Table.Row
                key={t.id}
                className="teams-row-clickable"
                onClick={() => navigate(`/teams/${t.id}`)}
              >
                <Table.RowHeaderCell>
                  <Text weight="medium">{t.name}</Text>
                  {t.notes && (
                    <Text as="div" size="1" color="gray">
                      {t.notes}
                    </Text>
                  )}
                </Table.RowHeaderCell>
                <Table.Cell align="center">{t.visits}</Table.Cell>
                <Table.Cell align="center">
                  {t.variantCount > 1 ? (
                    <Badge color="amber" variant="soft">
                      {t.variantCount}
                    </Badge>
                  ) : (
                    <Text color="gray">{t.variantCount}</Text>
                  )}
                </Table.Cell>
                <Table.Cell align="center">{fmtNum(t.avgPlayers)}</Table.Cell>
                <Table.Cell align="center">{fmtNum(t.avgPlacement)}</Table.Cell>
                <Table.Cell align="center">
                  {t.wins > 0 ? (
                    <Badge color="green" variant="soft">
                      {t.wins}
                    </Badge>
                  ) : (
                    <Text color="gray">0</Text>
                  )}
                </Table.Cell>
                <Table.Cell align="right">{fmtDate(t.lastSeen)}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      )}

      <Dialog.Root open={newOpen} onOpenChange={setNewOpen}>
        <Dialog.Content maxWidth="420px">
          <Dialog.Title>New team</Dialog.Title>
          <Dialog.Description size="2" mb="3" color="gray">
            Create a persistent team identity for this venue. You can attach per-event rows
            to it afterwards.
          </Dialog.Description>
          <TextField.Root
            placeholder="Team name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreate();
            }}
            autoFocus
          />
          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </Dialog.Close>
            <Button onClick={handleCreate} disabled={saving || !newName.trim()}>
              {saving ? 'Creating…' : 'Create'}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </div>
  );
};

export default TeamsList;
